import { api } from "@/features/utils/fetch-server";
import type {
  CallbackSlot,
  ClientMessage,
  MessageCategory,
  MessageEntry,
  MessageHistoryEntry,
  MessageResolution,
  MessageUrgency,
  MessageVisibility,
} from "../types";

export class MessagesAPI {
  // The inbox
  static listMessages = async (
    clientId: string,
    options?: {
      status?: string;
      assignedMemberId?: string;
      customerId?: string;
      category?: string;
      urgency?: string;
    },
  ): Promise<{ messages: ClientMessage[]; total: number }> => {
    const params = new URLSearchParams();
    if (options?.status) params.append("status", options.status);
    if (options?.assignedMemberId) {
      params.append("assigned_member_id", options.assignedMemberId);
    }
    if (options?.customerId) params.append("customer_id", options.customerId);
    if (options?.category) params.append("category", options.category);
    if (options?.urgency) params.append("urgency", options.urgency);

    const query = params.toString() ? `?${params}` : "";
    return api.get<{ messages: ClientMessage[]; total: number }>(
      `/api/messages/v1/${clientId}/messages${query}`,
    );
  };

  // The categories requests are filed under
  static listCategories = async (
    clientId: string,
  ): Promise<{ categories: MessageCategory[] }> => {
    return api.get<{ categories: MessageCategory[] }>(
      `/api/messages/v1/${clientId}/messages/categories`,
    );
  };

  static getMessage = async (
    clientId: string,
    id: string,
  ): Promise<{ message: ClientMessage }> => {
    return api.get<{ message: ClientMessage }>(
      `/api/messages/v1/${clientId}/messages/${id}`,
    );
  };

  // The call back time this request would get, before anybody commits to it
  static getCallbackSlot = async (
    clientId: string,
    id: string,
  ): Promise<CallbackSlot> => {
    return api.get<CallbackSlot>(
      `/api/messages/v1/${clientId}/messages/${id}/callback-slot`,
    );
  };

  // The request's timeline: comments, agent updates, events
  static listEntries = async (
    clientId: string,
    id: string,
  ): Promise<{ entries: MessageEntry[] }> => {
    return api.get<{ entries: MessageEntry[] }>(
      `/api/messages/v1/${clientId}/messages/${id}/entries`,
    );
  };

  // Write on the timeline, public (for the customer) or private, closing nothing
  static addEntry = async (
    clientId: string,
    id: string,
    data: {
      body: string;
      visibility: MessageVisibility;
      /** A public note, and the AI calls the customer to say it — the request stays open. */
      callback?: "now" | "later";
    },
  ): Promise<{ message: ClientMessage; entries: MessageEntry[] }> => {
    return api.post<
      { message: ClientMessage; entries: MessageEntry[] },
      typeof data
    >(`/api/messages/v1/${clientId}/messages/${id}/entries`, data);
  };

  // Make a comment public or private
  static setEntryVisibility = async (
    clientId: string,
    id: string,
    entryId: string,
    visibility: MessageVisibility,
  ): Promise<{ message: ClientMessage; entries: MessageEntry[] }> => {
    return api.put<
      { message: ClientMessage; entries: MessageEntry[] },
      { visibility: MessageVisibility }
    >(`/api/messages/v1/${clientId}/messages/${id}/entries/${entryId}`, {
      visibility,
    });
  };

  // Delete a comment: its text goes, a "deleted" line stays for the team
  static deleteEntry = async (
    clientId: string,
    id: string,
    entryId: string,
  ): Promise<{ message: ClientMessage; entries: MessageEntry[] }> => {
    return api.delete<{ message: ClientMessage; entries: MessageEntry[] }>(
      `/api/messages/v1/${clientId}/messages/${id}/entries/${entryId}`,
    );
  };

  // What changed on the request itself, and who changed it
  static getHistory = async (
    clientId: string,
    id: string,
  ): Promise<{ history: MessageHistoryEntry[] }> => {
    return api.get<{ history: MessageHistoryEntry[] }>(
      `/api/messages/v1/${clientId}/messages/${id}/history`,
    );
  };

  // Close a request, one of the three ways
  static handleMessage = async (
    clientId: string,
    id: string,
    data: {
      resolution: Exclude<MessageResolution, "">;
      reply?: string;
      callback_at?: number;
    },
  ): Promise<{ message: ClientMessage }> => {
    return api.post<{ message: ClientMessage }, typeof data>(
      `/api/messages/v1/${clientId}/messages/${id}/handle`,
      data,
    );
  };

  // Tick or untick one of the request's tasks
  static setTask = async (
    clientId: string,
    id: string,
    taskId: string,
    done: boolean,
  ): Promise<{ message: ClientMessage }> => {
    return api.put<{ message: ClientMessage }, { done: boolean }>(
      `/api/messages/v1/${clientId}/messages/${id}/tasks/${taskId}`,
      { done },
    );
  };

  // Another urgency, category or deadline (epoch ms) for a request
  static triageMessage = async (
    clientId: string,
    id: string,
    change: { urgency?: MessageUrgency; category?: string; due_at?: number },
  ): Promise<{ message: ClientMessage }> => {
    return api.put<{ message: ClientMessage }, typeof change>(
      `/api/messages/v1/${clientId}/messages/${id}/triage`,
      change,
    );
  };

  // Hand a request to somebody, or to nobody with an empty member_id
  static assignMessage = async (
    clientId: string,
    id: string,
    memberId: string,
  ): Promise<{ message: ClientMessage }> => {
    return api.put<{ message: ClientMessage }, { member_id: string }>(
      `/api/messages/v1/${clientId}/messages/${id}/assign`,
      { member_id: memberId },
    );
  };
}
